import type { CreditQualityBucket } from '../api/chartDataTypes';
import { SnapshotValueSelector, submitGuidance } from './SnapshotValueSelector';

const getBucketKey = (bucket: CreditQualityBucket) => bucket.grade;

export const CREDIT_QUALITY_SUBMIT_GUIDANCE = submitGuidance('credit rating');

type CreditQualitySelectorProps = {
    /** Snapshot buckets from the credit_quality payload. Order is irrelevant —
     *  the selector sorts by `grade` ascending internally. */
    buckets: CreditQualityBucket[];
    /** Prefix for the Credit Grade `<select>` id so multiple snapshot selectors
     *  on a page never collide. */
    idPrefix: string;
    /** Called only when the user presses Submit after picking a grade.
     *  Selecting the same grade again and re-submitting still fires so the
     *  parent can re-announce. */
    onSelectedBucketChange: (bucket: CreditQualityBucket) => void;
};

/**
 * Thin `SnapshotValueSelector` wrapper for the Credit Quality Breakdown
 * chart's grade picker — see `SnapshotValueSelector` for the shared
 * Submit-gating behavior (WCAG 2.2 3.2.2 On Input).
 */
export function CreditQualitySelector({ buckets, idPrefix, onSelectedBucketChange }: CreditQualitySelectorProps) {
    return (
        <SnapshotValueSelector<CreditQualityBucket>
            values={buckets}
            getKey={getBucketKey}
            idPrefix={idPrefix}
            fieldLabel='Data Series'
            placeholder='Select data series'
            selectedAriaLabel='Data series'
            hint='Select a credit rating, then press Submit to display its breakdown.'
            disabledSubmitAriaLabel='Submit — select a credit rating to continue'
            readySubmitAriaLabel={(grade) => `Submit and display breakdown for ${grade}`}
            onSubmit={onSelectedBucketChange}
        />
    );
}
